import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ClipboardList, Gauge, Hash, Package, PackagePlus, Palette, Printer, RotateCcw, ShieldCheck, Store, Tags, Unplug, Wrench, X } from 'lucide-react';
import '../../equipe2/components/TechnicianModal.css';
import { RichTextModal } from './DefeitosVerveRichTextModal';
import { DefeitosEquipCompatModal } from './DefeitosEquipCompatModal';
import { DefeitosColorPalettePopover, TIPO_OS_FIELDS, isDefeitoColorConfigured, joinTags, normOsFlag, normSev, parseTags } from './defeitosVerveTableUi';
import './DefeitosVerveNativeServiceModal.css';

const TIPO_ICONS = {
  allow_corrective: Wrench,
  allow_install: PackagePlus,
  allow_desinstall: Unplug,
  allow_preventive: ShieldCheck,
  allow_stock: Package,
  allow_recharge: RotateCcw,
  allow_measurement: Gauge,
  allow_counter: Store,
};
const TABS = [
  { id: 'geral', label: 'Geral', Icon: Tags, desc: 'Código no board, cor, classificação, criticidade e tipos de OS.' },
  { id: 'sugestao', label: 'Sugestão', Icon: ClipboardList, desc: 'Texto sugerido na abertura da OS.' },
  { id: 'equip', label: 'Equipamentos', Icon: Printer, desc: 'Modelos vinculados a este serviço.' },
];
const HELPS = {
  board: 'Defina o código de alerta exibido no board para este defeito.',
  color: 'Defina a cor de destaque do defeito no board.',
  classif: 'Informe tags separadas por Enter para classificar o defeito.',
  sev: 'Selecione o nível de criticidade aplicado a este defeito.',
  tipoos: 'Marque os tipos de ordem de serviço permitidos para este defeito.',
};
const SEV = [
  { v: 'critico', l: 'Crítico' },
  { v: 'moderado', l: 'Moderado' },
  { v: 'leve', l: 'Leve' },
];

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function emptyForm() {
  const tipos = {};
  TIPO_OS_FIELDS.forEach((f) => { tipos[f.db] = 1; });
  return { name: '', erpCode: '', isActive: true, board: '', color: '', tags: [], sev: 'leve', tipos };
}

function formFromRow(row) {
  const tipos = {};
  TIPO_OS_FIELDS.forEach((f) => { tipos[f.db] = normOsFlag(row?.[f.db]); });
  return {
    name: row?.TB01048_NOME || '',
    erpCode: '',
    isActive: row?.is_active !== false,
    board: String(row?.cod_alert || '').trim().slice(0, 4).toUpperCase(),
    color: String(row?.color_bg || '').trim(),
    tags: parseTags(row?.classification),
    sev: normSev(row?.severity_level),
    tipos,
  };
}

function enabledRow(row) {
  return row?.habilitado === 1 || row?.habilitado === true;
}

function TmSwitch({ checked, onChange, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} className={`tm-switch${checked ? ' is-on' : ''}`} onClick={() => !disabled && onChange(!checked)}>
      <span className="tm-switch-thumb" />
    </button>
  );
}

async function saveJson(url, method, body) {
  const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.message || 'Não foi possível salvar');
  return json;
}

export function DefeitosVerveNativeServiceModal({
  open,
  mode,
  codigo,
  row,
  nativeMode = false,
  baseUrl,
  onClose,
  onSaved,
  onLinkedCountChange,
}) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(emptyForm);
  const [tagInput, setTagInput] = useState('');
  const [persistedCode, setPersistedCode] = useState(null);
  const [suggestionHtml, setSuggestionHtml] = useState('');
  const [tab, setTab] = useState('geral');
  const [colorOpen, setColorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const htmlGetterRef = useRef(null);
  const colorBtnRef = useRef(null);
  const rowRef = useRef(row);
  rowRef.current = row;
  const code = persistedCode || (isEdit ? codigo : null);
  const bindingsReady = !isEdit || enabledRow(row) || !!persistedCode;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !colorOpen) return undefined;
    const onDoc = (e) => {
      if (colorBtnRef.current?.contains(e.target)) return;
      if (e.target.closest?.('.defeitos-vv-color-popover')) return;
      setColorOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open, colorOpen]);

  useEffect(() => {
    if (!open) return undefined;
    setError(null);
    setColorOpen(false);
    setTab('geral');
    setTagInput('');
    setPersistedCode(null);
    const current = rowRef.current;
    setForm(isEdit ? formFromRow(current) : emptyForm());
    setSuggestionHtml(isEdit ? (current?.auto_solution_suggestions || '') : '');
    return undefined;
  }, [open, codigo, isEdit]);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    setTagInput('');
    setForm((prev) => (prev.tags.includes(t) ? prev : { ...prev, tags: [...prev.tags, t] }));
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.name.trim()) {
      setTab('geral');
      setError('Informe o nome do serviço');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let wrote = false;
      let nextCode = code;
      if (!nextCode) {
        if (!nativeMode) throw new Error('O cadastro novo está disponível na operação nativa.');
        const created = await saveJson(`${baseUrl}/api/config/defeitos-verve/native/services`, 'POST', {
          name: form.name.trim(),
          erpCode: form.erpCode.trim(),
        });
        nextCode = created.data?.codigo;
        if (!nextCode) throw new Error('Serviço cadastrado sem código');
        setPersistedCode(nextCode);
        wrote = true;
      } else if (nativeMode) {
        await saveJson(`${baseUrl}/api/config/defeitos-verve/native/services/${encodeURIComponent(nextCode)}`, 'PUT', {
          name: form.name.trim(),
          isActive: form.isActive,
        });
        wrote = true;
      }
      if (bindingsReady || !isEdit) {
        const body = {
          codDefeito: nextCode,
          codAlert: form.board,
          colorBg: form.color,
          classification: joinTags(form.tags),
          severityLevel: form.sev,
          autoSolutionSuggestions: htmlGetterRef.current ? htmlGetterRef.current() : suggestionHtml,
        };
        TIPO_OS_FIELDS.forEach((f) => { body[f.api] = normOsFlag(form.tipos[f.db]); });
        await saveJson(`${baseUrl}/api/config/defeitos-verve/campos`, 'PATCH', body);
        wrote = true;
      }
      if (!wrote) throw new Error('Habilite o serviço na tabela para gravar estes dados.');
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const current = TABS.find((item) => item.id === tab) || TABS[0];
  const equipRow = code ? { TB01048_CODIGO: code, TB01048_NOME: form.name || row?.TB01048_NOME || '' } : null;

  return ReactDOM.createPortal(
    <div className="tm-overlay tm-svc" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="tm-modal" role="dialog" aria-modal="true" aria-label={isEdit ? 'Editar serviço' : 'Novo serviço'} onMouseDown={(e) => e.stopPropagation()}>
        <aside className="tm-sidebar">
          <div className="tm-profile">
            <div className="tm-avatar is-empty" aria-hidden><Tags size={22} strokeWidth={1.75} /></div>
            <div className="tm-profile-text">
              {isEdit ? <span className="tm-eyebrow">Editar serviço</span> : null}
              <h2 className="tm-profile-name" title={isEdit ? form.name : 'Novo serviço'}>{isEdit ? (form.name || 'Serviço') : 'Novo serviço'}</h2>
              <p className="tm-profile-role">{isEdit ? 'Serviço' : 'Será criado já habilitado'}</p>
            </div>
            <div className="tm-profile-chips">
              <span className="tm-chip"><Hash size={12} strokeWidth={1.75} /> {code || form.erpCode || 'Sem código'}</span>
            </div>
          </div>
          <nav className="tm-nav tm-cond-nav" role="tablist" aria-label="Seções do serviço">
            {TABS.map((item) => {
              const NavIcon = item.Icon;
              return (
                <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} className={`tm-nav-item${tab === item.id ? ' is-active' : ''}`} onClick={() => setTab(item.id)}>
                  <NavIcon className="tm-icon" size={16} strokeWidth={1.75} />
                  <span className="tm-nav-label">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>
        <section className="tm-main">
          <header className="tm-main-head">
            <div className="tm-main-heading">
              <h2 className="tm-main-title">{current.label}</h2>
              <span className="tm-main-desc">{current.desc}</span>
            </div>
            <button type="button" className="tm-icon-btn" onClick={onClose} aria-label="Fechar"><X size={16} /></button>
          </header>
          <div className={`tm-body${tab === 'geral' ? '' : ' tm-svc-fill'}`}>
            {tab === 'geral' ? (
              <>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Identificação</h3>
                    <p className="tm-section-desc">{nativeMode ? 'Nome do serviço nesta base.' : 'O nome vem do cadastro do ERP.'}</p>
                  </header>
                  <div className="tm-section-body">
                    <div className="tm-grid">
                      <label className="tm-field">
                        <span className="tm-label">Nome *</span>
                        <input className="tm-control" value={form.name} maxLength={60} disabled={isEdit && !nativeMode} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                      </label>
                      {!code ? (
                        <label className="tm-field">
                          <span className="tm-label">Código no ERP</span>
                          <input className="tm-control" value={form.erpCode} maxLength={4} spellCheck={false} placeholder="Opcional" onChange={(e) => setForm((p) => ({ ...p, erpCode: e.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 4).toUpperCase() }))} />
                        </label>
                      ) : (
                        <label className="tm-field">
                          <span className="tm-label">Código</span>
                          <input className="tm-control" value={code} disabled />
                        </label>
                      )}
                    </div>
                    {nativeMode && code ? (
                      <label className="tm-toggle-card" style={{ marginTop: 12 }}>
                        <span className="tm-toggle-title">Ativo</span>
                        <TmSwitch checked={form.isActive} onChange={(v) => setForm((p) => ({ ...p, isActive: v }))} />
                      </label>
                    ) : null}
                  </div>
                </section>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Cód. board</h3>
                    <p className="tm-section-desc">{HELPS.board}</p>
                  </header>
                  <div className="tm-section-body">
                    {!bindingsReady ? <p className="tm-hint">Habilite o serviço na tabela para gravar estes dados.</p> : null}
                    <label className="tm-field">
                      <span className="tm-label">Código</span>
                      <input className="tm-control" value={form.board} maxLength={4} disabled={!bindingsReady} spellCheck={false} placeholder="Ex: AB01" onChange={(e) => setForm((p) => ({ ...p, board: e.target.value.slice(0, 4).toUpperCase() }))} />
                    </label>
                  </div>
                </section>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Cor</h3>
                    <p className="tm-section-desc">{HELPS.color}</p>
                  </header>
                  <div className="tm-section-body">
                    <button
                      type="button"
                      ref={colorBtnRef}
                      className="tm-svc-color-btn"
                      disabled={!bindingsReady}
                      aria-label="Abrir cores"
                      aria-expanded={colorOpen}
                      onClick={() => setColorOpen((open) => !open)}
                    >
                      {isDefeitoColorConfigured(form.color) ? (
                        <span className="defeitos-vv-color-swatch dv2-color-swatch-only" style={{ background: form.color }} aria-hidden />
                      ) : (
                        <span className="defeitos-vv-muted">—</span>
                      )}
                      <span className="dv2-color-config-btn" aria-hidden>
                        <Palette size={14} strokeWidth={2} />
                      </span>
                    </button>
                    <DefeitosColorPalettePopover
                      open={colorOpen}
                      anchorRef={colorBtnRef}
                      draftColor={form.color}
                      onDraftChange={(hex) => setForm((p) => ({ ...p, color: hex }))}
                      onClose={() => setColorOpen(false)}
                    />
                  </div>
                </section>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Classificação</h3>
                    <p className="tm-section-desc">{HELPS.classif}</p>
                  </header>
                  <div className="tm-section-body">
                    <div className="dv2-classif-tag-box">
                      {form.tags.map((t) => (
                        <span key={t} className="defeitos-vv-tag dv2-classif-tag-chip">
                          {t}
                          <button type="button" className="defeitos-vv-tag-remove" disabled={!bindingsReady} aria-label={`Remover ${t}`} onClick={() => setForm((p) => ({ ...p, tags: p.tags.filter((x) => x !== t) }))}>×</button>
                        </span>
                      ))}
                      <input className="dv2-classif-tag-input" value={tagInput} disabled={!bindingsReady} placeholder="Tag e Enter" onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
                    </div>
                  </div>
                </section>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Criticidade</h3>
                    <p className="tm-section-desc">{HELPS.sev}</p>
                  </header>
                  <div className="tm-section-body">
                    <div className="user-form-toggle-wrap" role="group" aria-label="Criticidade">
                      {SEV.map((opt) => (
                        <button key={opt.v} type="button" className={`user-form-toggle-pill${form.sev === opt.v ? ' active' : ''}`} aria-pressed={form.sev === opt.v} disabled={!bindingsReady} onClick={() => setForm((p) => ({ ...p, sev: opt.v }))}>{opt.l}</button>
                      ))}
                    </div>
                  </div>
                </section>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Tipos de OS</h3>
                    <p className="tm-section-desc">{HELPS.tipoos}</p>
                  </header>
                  <div className="tm-section-body">
                    <div className="user-form-toggle-wrap" role="group" aria-label="Tipos de ordem de serviço">
                      {TIPO_OS_FIELDS.map((item) => {
                        const active = normOsFlag(form.tipos[item.db]) === 1;
                        const Icon = TIPO_ICONS[item.db];
                        return (
                          <button key={item.db} type="button" className={`user-form-toggle-pill${active ? ' active' : ''}`} aria-pressed={active} disabled={!bindingsReady} onClick={() => setForm((p) => ({ ...p, tipos: { ...p.tipos, [item.db]: active ? 0 : 1 } }))}>
                            {Icon ? <Icon size={14} strokeWidth={2} aria-hidden /> : null}
                            <span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>
              </>
            ) : null}
            <div className="tm-svc-fill-child" hidden={tab !== 'sugestao'}>
              {bindingsReady ? (
                <RichTextModal open embedded htmlGetterRef={htmlGetterRef} initialHtml={suggestionHtml} title="" onClose={() => {}} onSave={() => {}} saving={false} />
              ) : <p className="tm-hint" style={{ padding: 24 }}>Habilite o serviço na tabela para editar a sugestão.</p>}
            </div>
            <div className="tm-svc-fill-child" hidden={tab !== 'equip'}>
              {equipRow ? (
                <DefeitosEquipCompatModal open embedded row={equipRow} onClose={() => {}} onLinkedCountChange={onLinkedCountChange} />
              ) : <p className="tm-hint" style={{ padding: 24 }}>Salve o serviço para vincular equipamentos.</p>}
            </div>
          </div>
          {tab !== 'equip' ? (
            <footer className="tm-footer">
              <span className={`tm-footer-status${error ? ' is-danger' : ''}`}>{error || null}</span>
              <div className="tm-footer-actions">
                <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onClose} disabled={saving}>Cancelar</button>
                <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={handleSave} disabled={saving}>{saving ? 'Salvando…' : code ? 'Salvar' : 'Cadastrar'}</button>
              </div>
            </footer>
          ) : null}
        </section>
      </div>
    </div>,
    document.body,
  );
}

export default DefeitosVerveNativeServiceModal;
